import React from "react";
import { Card, Button } from "antd";
import { ISurveyTheme } from "../../services/nestjs-base-api/types";
import { textColorOn, mutedTextColorOn, subtleBorderOn, isLightColor } from "../../utils/themeContrast";

interface SurveyPreviewProps {
    surveyName: string;
    brandName: string;
    theme: ISurveyTheme;
}

const SurveyPreview: React.FC<SurveyPreviewProps> = ({
    surveyName,
    brandName,
    theme
}) => {
    const pageTextColor = textColorOn(theme.backgroundColor);
    const pageMutedColor = mutedTextColorOn(theme.backgroundColor);
    const cardTextColor = textColorOn(theme.cardColor);
    const cardMutedColor = mutedTextColorOn(theme.cardColor);
    const optionTextColor = textColorOn(theme.primaryColor);
    const optionCircleBg = isLightColor(theme.primaryColor)
        ? 'rgba(33, 33, 33, 0.1)'
        : 'rgba(255, 255, 255, 0.2)';
    const optionEmptyDotColor = subtleBorderOn(theme.primaryColor);

    const previewStyle: React.CSSProperties = {
        backgroundColor: theme.backgroundColor,
        color: pageTextColor,
        borderRadius: 8,
        padding: 20,
        minHeight: 600,
        position: 'relative'
    };

    const cardStyle: React.CSSProperties = {
        backgroundColor: theme.cardColor,
        border: 'none',
        borderRadius: 8,
        marginBottom: 16
    };

    const primaryButtonStyle: React.CSSProperties = {
        backgroundColor: theme.primaryColor,
        borderColor: theme.primaryColor,
        color: textColorOn(theme.primaryColor)
    };

    const secondaryButtonStyle: React.CSSProperties = {
        backgroundColor: theme.secondaryColor,
        borderColor: theme.secondaryColor,
        color: textColorOn(theme.secondaryColor)
    };

    const likertOptions = [
        { label: 'Strongly Disagree', dots: 1, color: '#ef4444' },
        { label: 'Disagree', dots: 2, color: '#f97316' },
        { label: 'Somewhat Disagree', dots: 3, color: '#eab308' },
        { label: 'Neutral', dots: 4, color: '#eab308' },
        { label: 'Somewhat Agree', dots: 5, color: '#84cc16' },
        { label: 'Agree', dots: 6, color: '#22c55e' },
        { label: 'Strongly Agree', dots: 7, color: '#22c55e' }
    ];

    return (
        <div style={previewStyle}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 24, color: pageMutedColor, fontSize: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Question 2 of 10</span>
                    <span>10% Complete</span>
                </div>
            </div>

            {/* Question Card */}
            <Card style={cardStyle} styles={{ body: { padding: 20 } }}>
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <h3 style={{ color: cardTextColor, margin: 0, fontSize: '18px', fontWeight: '600' }}>
                        {brandName || 'July'} is available when I want to buy it
                    </h3>
                </div>

                {/* Likert Scale Options */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {likertOptions.map((option, index) => (
                        <div
                            key={index}
                            style={{
                                backgroundColor: theme.primaryColor,
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                cursor: 'pointer',
                                opacity: index === 4 ? 1 : 0.8,
                                transition: 'opacity 0.2s'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        backgroundColor: optionCircleBg,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: optionTextColor,
                                        fontSize: '14px',
                                        fontWeight: '600'
                                    }}>
                                        {index + 1}
                                    </div>
                                    <span style={{ color: optionTextColor, fontSize: '14px', fontWeight: '500' }}>
                                        {option.label}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: 2 }}>
                                    {Array.from({ length: 7 }, (_, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                width: '6px',
                                                height: '6px',
                                                borderRadius: '50%',
                                                backgroundColor: i < option.dots ? option.color : optionEmptyDotColor
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div style={{ textAlign: 'left', marginTop: 24 }}>
                    <Button
                        style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: cardMutedColor,
                            padding: '8px 0',
                            fontSize: '14px'
                        }}
                    >
                        <span style={{ marginRight: 8 }}>←</span>
                        Previous question
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default SurveyPreview;
